import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, TrendingUp, Eye, Users, Target, Share2,
  BarChart3, Activity, Award, Globe, Zap, Calendar,
  ExternalLink, RefreshCw, Download, Sparkles
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell,
  Area, AreaChart
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'];

const JobAnalyticsPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { token, isAuthenticated, loading: authLoading } = useAuth();
  const { toast } = useToast();
  
  const [job, setJob] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
      return;
    }
    if (isAuthenticated) {
      fetchAnalytics();
    }
  }, [isAuthenticated, authLoading, jobId]);

  const fetchAnalytics = async (showRefreshToast = false) => {
    if (showRefreshToast) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    
    try {
      // Fetch job details
      const jobResponse = await fetch(`${BACKEND_URL}/api/jobs/${jobId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (jobResponse.ok) {
        const jobData = await jobResponse.json();
        setJob(jobData.job);
      }

      // Fetch analytics
      const analyticsResponse = await fetch(`${BACKEND_URL}/api/jobs/${jobId}/analytics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (analyticsResponse.ok) {
        const analyticsData = await analyticsResponse.json();
        setAnalytics(analyticsData.analytics);
        
        if (showRefreshToast) {
          toast({
            title: 'Analytics Refreshed',
            description: 'Latest data has been loaded successfully',
          });
        }
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast({
        title: 'Error',
        description: 'Failed to load analytics',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const getPerformanceColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-blue-600 bg-blue-100';
    if (score >= 40) return 'text-orange-600 bg-orange-100';
    return 'text-red-600 bg-red-100';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="min-h-screen bg-slate-50 py-8 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-slate-600">No analytics data available</p>
        </div>
      </div>
    );
  }

  // Prepare chart data
  const dailyChartData = analytics.daily_breakdown?.slice(-14) || []; // Last 14 days
  const sourceChartData = analytics.top_sources?.map(s => ({
    name: s.source.replace('_', ' ').toUpperCase(),
    value: s.count
  })) || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 py-8 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <Button 
              onClick={() => navigate('/my-jobs')} 
              variant="outline"
              size="sm"
              className="hover:bg-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to My Jobs
            </Button>
            
            <Button 
              onClick={() => fetchAnalytics(true)} 
              variant="outline"
              size="sm"
              disabled={refreshing}
              className="hover:bg-white transition-colors"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>
          
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <BarChart3 className="w-8 h-8 text-blue-600" />
                <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Job Analytics
                </h1>
              </div>
              {job && (
                <div className="space-y-1">
                  <p className="text-xl text-slate-800 font-semibold">
                    {job.title}
                  </p>
                  <p className="text-sm text-slate-600">
                    {job.company_name} • Posted {analytics?.days_active || 0} days ago
                  </p>
                </div>
              )}
            </div>
            
            {/* Performance Score */}
            <div className="text-center p-6 bg-white rounded-2xl shadow-lg border-2 border-blue-100">
              <Sparkles className="w-6 h-6 text-yellow-500 mx-auto mb-2" />
              <div className={`text-6xl font-bold ${getPerformanceColor(analytics.performance_score).split(' ')[0]} mb-2 transition-all`}>
                {analytics.performance_score}
              </div>
              <Badge className={`${getPerformanceColor(analytics.performance_score)} text-sm px-4 py-1`}>
                {analytics.performance_rating}
              </Badge>
              <p className="text-xs text-slate-600 mt-2 font-medium">Performance Score</p>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-2 border-blue-100 hover:border-blue-300 transition-all hover:shadow-xl hover:-translate-y-1 duration-300 bg-gradient-to-br from-white to-blue-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 font-medium mb-1">Total Views</p>
                  <p className="text-4xl font-bold text-blue-600 mb-1">{analytics.total_views}</p>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">
                      {analytics.views_today} today
                    </Badge>
                  </div>
                </div>
                <div className="p-4 bg-blue-100 rounded-full">
                  <Eye className="w-8 h-8 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-green-100 hover:border-green-300 transition-all hover:shadow-xl hover:-translate-y-1 duration-300 bg-gradient-to-br from-white to-green-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 font-medium mb-1">Applications</p>
                  <p className="text-4xl font-bold text-green-600 mb-1">{analytics.total_applications}</p>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">
                      {analytics.applications_today} today
                    </Badge>
                  </div>
                </div>
                <div className="p-4 bg-green-100 rounded-full">
                  <Users className="w-8 h-8 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-purple-100 hover:border-purple-300 transition-all hover:shadow-xl hover:-translate-y-1 duration-300 bg-gradient-to-br from-white to-purple-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 font-medium mb-1">Conversion Rate</p>
                  <p className="text-4xl font-bold text-purple-600 mb-1">{analytics.view_to_apply_rate}%</p>
                  <p className="text-xs text-slate-500">
                    Views to applications
                  </p>
                </div>
                <div className="p-4 bg-purple-100 rounded-full">
                  <Target className="w-8 h-8 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-orange-100 hover:border-orange-300 transition-all hover:shadow-xl hover:-translate-y-1 duration-300 bg-gradient-to-br from-white to-orange-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 font-medium mb-1">Reach Score</p>
                  <p className="text-4xl font-bold text-orange-600 mb-1">{analytics.reach_score}</p>
                  <p className="text-xs text-slate-500">
                    Distribution power
                  </p>
                </div>
                <div className="p-4 bg-orange-100 rounded-full">
                  <Globe className="w-8 h-8 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Daily Trend */}
          <Card className="border-2 border-slate-100 hover:shadow-xl transition-shadow bg-white">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50">
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-blue-600" />
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Views & Applications Trend
                </span>
              </CardTitle>
              <p className="text-xs text-slate-600 mt-1">Last 14 days performance</p>
            </CardHeader>
            <CardContent className="pt-6">
              {dailyChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={dailyChartData}>
                    <defs>
                      <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorApplications" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 11, fill: '#64748b' }}
                      tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    />
                    <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '2px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                      }}
                    />
                    <Legend wrapperStyle={{ paddingTop: '10px' }} />
                    <Area 
                      type="monotone" 
                      dataKey="views" 
                      stroke="#3b82f6" 
                      strokeWidth={3}
                      fillOpacity={1} 
                      fill="url(#colorViews)" 
                      name="Views" 
                    />
                    <Area 
                      type="monotone" 
                      dataKey="applications" 
                      stroke="#10b981" 
                      strokeWidth={3}
                      fillOpacity={1} 
                      fill="url(#colorApplications)" 
                      name="Applications" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex flex-col items-center justify-center h-[280px] text-slate-400">
                  <Activity className="w-16 h-16 mb-3 opacity-30" />
                  <p className="text-sm">No trend data available yet</p>
                  <p className="text-xs mt-1">Data will appear as your job gets views</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Traffic Sources */}
          <Card className="border-2 border-slate-100 hover:shadow-xl transition-shadow bg-white">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
              <CardTitle className="flex items-center gap-2">
                <Share2 className="w-5 h-5 text-purple-600" />
                <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  Traffic Sources
                </span>
              </CardTitle>
              <p className="text-xs text-slate-600 mt-1">Where your views come from</p>
            </CardHeader>
            <CardContent className="pt-6">
              {sourceChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={sourceChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(entry) => `${entry.name}: ${entry.value}`}
                      outerRadius={90}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {sourceChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '2px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex flex-col items-center justify-center h-[280px] text-slate-400">
                  <Share2 className="w-16 h-16 mb-3 opacity-30" />
                  <p className="text-sm">No source data available yet</p>
                  <p className="text-xs mt-1">Source tracking will begin with first view</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Distribution & Engagement */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Distribution Channels */}
          <Card className="border-2 border-slate-100 hover:shadow-xl transition-shadow bg-white">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50">
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-blue-600" />
                <span className="bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
                  Distribution Channels
                </span>
              </CardTitle>
              <p className="text-xs text-slate-600 mt-1">Where your job is posted</p>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-3">
                {analytics.distribution_channels?.map((channel, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-4 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl border border-blue-100 hover:border-blue-300 transition-all hover:shadow-md">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center shadow-md">
                      <ExternalLink className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">{channel}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        <p className="text-xs text-green-600 font-medium">Active & Live</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Additional Metrics */}
          <Card className="border-2 border-slate-100 hover:shadow-xl transition-shadow bg-white">
            <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50">
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-green-600" />
                <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                  Key Metrics
                </span>
              </CardTitle>
              <p className="text-xs text-slate-600 mt-1">Additional performance indicators</p>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-3">
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-slate-50 to-purple-50 rounded-xl border border-purple-100 hover:border-purple-300 transition-all">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Calendar className="w-5 h-5 text-purple-600" />
                    </div>
                    <span className="text-sm font-semibold text-slate-700">Days Active</span>
                  </div>
                  <span className="text-2xl font-bold text-purple-600">{analytics.days_active}</span>
                </div>

                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl border border-blue-100 hover:border-blue-300 transition-all">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <TrendingUp className="w-5 h-5 text-blue-600" />
                    </div>
                    <span className="text-sm font-semibold text-slate-700">Avg Views/Day</span>
                  </div>
                  <span className="text-2xl font-bold text-blue-600">{analytics.avg_views_per_day}</span>
                </div>

                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-slate-50 to-indigo-50 rounded-xl border border-indigo-100 hover:border-indigo-300 transition-all">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-indigo-100 rounded-lg">
                      <Zap className="w-5 h-5 text-indigo-600" />
                    </div>
                    <span className="text-sm font-semibold text-slate-700">Engagement Rate</span>
                  </div>
                  <span className="text-2xl font-bold text-indigo-600">{analytics.engagement_rate}</span>
                </div>

                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-slate-50 to-green-50 rounded-xl border border-green-100 hover:border-green-300 transition-all">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Eye className="w-5 h-5 text-green-600" />
                    </div>
                    <span className="text-sm font-semibold text-slate-700">Unique Visitors</span>
                  </div>
                  <span className="text-2xl font-bold text-green-600">{analytics.unique_views}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Insights */}
        <Card className="border-2 border-yellow-100 hover:shadow-xl transition-shadow bg-white">
          <CardHeader className="bg-gradient-to-r from-yellow-50 via-orange-50 to-red-50">
            <CardTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-yellow-600" />
              <span className="bg-gradient-to-r from-yellow-600 to-red-600 bg-clip-text text-transparent">
                AI-Powered Performance Insights
              </span>
            </CardTitle>
            <p className="text-xs text-slate-600 mt-1">Actionable recommendations to improve your job posting</p>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-5 bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200 rounded-xl hover:shadow-lg transition-all hover:-translate-y-1">
                <div className="flex items-center gap-2 mb-3">
                  <Eye className="w-5 h-5 text-blue-600" />
                  <h4 className="font-bold text-blue-900">Visibility</h4>
                </div>
                <p className="text-sm text-slate-700 leading-relaxed">
                  {analytics.total_views > 50 ? 
                    '🎉 Excellent visibility! Your job is getting great exposure across multiple channels.' :
                    analytics.total_views > 20 ?
                    '👍 Good visibility. Consider sharing on more channels to increase reach.' :
                    '💡 Low visibility detected. Share your job on LinkedIn and other platforms to boost exposure.'}
                </p>
              </div>

              <div className="p-5 bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-200 rounded-xl hover:shadow-lg transition-all hover:-translate-y-1">
                <div className="flex items-center gap-2 mb-3">
                  <Target className="w-5 h-5 text-green-600" />
                  <h4 className="font-bold text-green-900">Application Rate</h4>
                </div>
                <p className="text-sm text-slate-700 leading-relaxed">
                  {analytics.view_to_apply_rate > 5 ?
                    '⭐ Great conversion! Candidates are highly interested in this role.' :
                    analytics.view_to_apply_rate > 2 ?
                    '📝 Average conversion. Try improving the job description and highlighting benefits.' :
                    '⚠️ Low conversion. Review salary range and requirements to attract more applicants.'}
                </p>
              </div>

              <div className="p-5 bg-gradient-to-br from-purple-50 to-purple-100 border-2 border-purple-200 rounded-xl hover:shadow-lg transition-all hover:-translate-y-1">
                <div className="flex items-center gap-2 mb-3">
                  <Globe className="w-5 h-5 text-purple-600" />
                  <h4 className="font-bold text-purple-900">Distribution Reach</h4>
                </div>
                <p className="text-sm text-slate-700 leading-relaxed">
                  Your job is posted on <strong>{analytics.distribution_channels?.length || 1} platform(s)</strong>. 
                  {(analytics.distribution_channels?.length || 1) > 1 ?
                    ' 🚀 Great distribution! You\'re maximizing your reach.' :
                    ' 📢 Consider adding more distribution channels to expand your reach.'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default JobAnalyticsPage;
